// entities/id.ts
class ID {
  #value;
  constructor(value) {
    if (!value || !value.trim().length)
      throw new Error("ID can not be empty");
    this.#value = value;
  }
  toString() {
    return this.#value;
  }
  valueOf() {
    return this.#value;
  }
}
// entities/finding.ts
class ItemID extends ID {
  __type = Symbol.for("ItemID");
}
var Rarity;
((Rarity2) => {
  Rarity2[Rarity2["Common"] = 0] = "Common";
  Rarity2[Rarity2["Uncommon"] = 1] = "Uncommon";
  Rarity2[Rarity2["Rare"] = 2] = "Rare";
  Rarity2[Rarity2["Legendary"] = 3] = "Legendary";
})(Rarity ||= {});
var COLLECTION_IDS = ["construction", "container", "stationery", "storage", "technical", "elite"];

class Finding {
  id;
  typeId;
  weight;
  rarity;
  category;
  collectionId;
  constructor(params) {
    this.id = params.id;
    this.typeId = params.typeId;
    this.weight = params.weight;
    this.rarity = params.rarity;
    this.category = params.category;
    this.collectionId = params.collectionId;
  }
}

class Consumable extends Finding {
  constructor(params) {
    super({
      ...params,
      category: "consumable"
    });
  }
}

class EnergyDrink extends Consumable {
  use(player) {
    player.chargeBattery();
  }
}

class Medkit extends Consumable {
  use(player) {
    player.heal(0.3);
  }
}

class Food extends Consumable {
  use(player) {
    player.heal(0.1);
  }
}

class Filter extends Consumable {
  use(player) {
    player.reduceRadiation(0.2);
  }
}

class CollectionSet {
  id;
  collectionId;
  items;
  constructor(id, collectionId, items) {
    this.id = id;
    this.collectionId = collectionId;
    this.items = items;
  }
  isComplete() {
    return this.items.length >= 5;
  }
}

class AccessTool extends Finding {
  constructor(params) {
    super({
      ...params,
      category: "access"
    });
  }
}

class Lockpick extends AccessTool {
  constructor(params) {
    super(params);
  }
}

class KeyCard extends AccessTool {
  level;
  locationId;
  constructor(params, level, locationId) {
    super(params);
    this.level = level;
    this.locationId = locationId;
  }
}
// entities/entity.ts
class Entity {
  #id;
  #events;
  get id() {
    return this.#id;
  }
  constructor(id) {
    this.#id = id;
    this.#events = [];
  }
  registerEvent(event) {
    this.#events.push(event);
  }
  flushEvents() {
    const events = this.#events;
    this.#events = [];
    return events;
  }
  onTick(_delta) {}
  equals(entity) {
    return Entity.equals(this, entity);
  }
  static equals(a, b) {
    return a.id.constructor === b.id.constructor && String(a.id) === String(b.id);
  }
}

// entities/player.ts
var ENERGY_PER_ACTION = 0.01;
var ENERGY_REGEN_PER_MS = 0.01 / 1e4;

class PlayerID extends ID {
  __type = Symbol.for("PlayerID");
}

class Player extends Entity {
  #energy;
  #radiation;
  #rating;
  #health;
  #money;
  #lastExpenseAt;
  #isDead = false;
  _inventory;
  _location;
  get inventory() {
    return this._inventory;
  }
  get energy() {
    return this.#energy;
  }
  get radiation() {
    return this.#radiation;
  }
  get health() {
    return this.#health;
  }
  get rating() {
    return this.#rating;
  }
  get money() {
    return this.#money;
  }
  get lastExpenseAt() {
    return this.#lastExpenseAt;
  }
  setLastExpenseAt(timestamp) {
    this.#lastExpenseAt = timestamp;
  }
  get isDead() {
    return this.#isDead || this.#health <= 0;
  }
  constructor(id, location, energy, radiation, rating = 0, inventory = [], health = 1, money = 0, lastExpenseAt = Date.now()) {
    super(id);
    this.#energy = Math.max(0, Math.min(1, energy));
    this.#radiation = Math.max(0, Math.min(1, radiation));
    this.#rating = rating;
    this.#health = Math.max(0, Math.min(1, health));
    this.#money = money;
    this._location = location;
    this._inventory = inventory || [];
    this.#lastExpenseAt = lastExpenseAt;
  }
  heal(amount) {
    this.#health = Math.max(0, Math.min(1, this.#health + amount));
    if (this.#health < 0.000001) {
      this.#health = 0;
    }
  }
  reduceRadiation(amount) {
    this.#radiation = Math.max(0, this.#radiation - amount);
  }
  chargeBattery() {
    this.#energy = 1;
  }
  moveTo(location) {
    if (this.#energy < ENERGY_PER_ACTION) {
      throw new Error("Not enough energy to move");
    }
    if (!location.canEnter(this)) {
      throw new Error("Access denied to location");
    }
    this._location = location;
    this.#energy = Math.max(0, this.#energy - ENERGY_PER_ACTION);
  }
  rank(amount) {
    this.#rating = Math.max(0, this.#rating + amount);
  }
  addMoney(amount) {
    this.#money += amount;
  }
  withdrawMoney(amount) {
    if (this.#money >= amount) {
      this.#money -= amount;
      return true;
    }
    this.#money = 0;
    return false;
  }
  applyStarvingEffect(damage) {
    this.heal(-damage);
  }
  removeItems(items) {
    this._inventory = this._inventory.filter((i) => !items.includes(i));
  }
  onTick(delta) {
    if (this.isDead)
      return;
    const energyRegen = ENERGY_REGEN_PER_MS * delta;
    this.#energy = Math.min(1, this.#energy + energyRegen);
    const radiationDamage = this._location.radiationEffect * (delta / 1000);
    this.#radiation = Math.min(1, Math.max(0, this.#radiation + radiationDamage));
    if (this.#radiation >= 1) {
      this.#isDead = true;
    }
  }
}
// entities/patrol.ts
class Patrol extends Player {
  #detainedStalker = null;
  #bribeTimer = 0;
  #awaitingBribeDecision = false;
  #pendingBribe = [];
  catchStalker(stalker) {
    if (!this._location.isBuilding) {
      throw new Error("Can only catch stalkers inside buildings");
    }
    this.#detainedStalker = stalker;
    stalker.onDetained(this);
  }
  onBribeOffered(_stalker, items) {
    this.#awaitingBribeDecision = true;
    this.#pendingBribe = items;
    this.#bribeTimer = 0;
  }
  acceptBribe() {
    if (!this.#awaitingBribeDecision || !this.#detainedStalker)
      return;
    this._inventory.push(...this.#pendingBribe);
    this.#detainedStalker.onBribeAccepted();
    this.#resetDetention();
  }
  declineBribe() {
    if (!this.#awaitingBribeDecision || !this.#detainedStalker)
      return;
    this.#detainedStalker.onBribeDeclined();
    this.#resetDetention();
  }
  onSurrender(stalker) {
    this.confiscate(stalker);
    this.#resetDetention();
  }
  confiscate(stalker) {
    const items = [...stalker.inventory];
    const confiscated = [];
    for (const item of items) {
      if (Math.random() > 0.5) {
        confiscated.push(item);
      }
    }
    stalker.removeItems(confiscated);
    this._inventory.push(...confiscated);
    this.rank(20);
  }
  #resetDetention() {
    this.#detainedStalker = null;
    this.#awaitingBribeDecision = false;
    this.#pendingBribe = [];
    this.#bribeTimer = 0;
  }
  onTick(delta) {
    super.onTick(delta);
    if (this.#awaitingBribeDecision) {
      this.#bribeTimer += delta;
      if (this.#bribeTimer >= 1e4) {
        this.declineBribe();
      }
    }
  }
}

// entities/location.ts
class LocationID extends ID {
  __type = Symbol.for("LocationID");
}

class Location {
  #id;
  #radiation;
  #inventory;
  #lock = null;
  #requiredAccessLevel = 0;
  #stormId = null;
  get id() {
    return this.#id;
  }
  get radiationEffect() {
    return this.#radiation;
  }
  constructor(id, radiationFactor = 0.001, inventory = []) {
    this.#id = id;
    this.#radiation = radiationFactor;
    this.#inventory = inventory;
  }
  getFindings() {
    return this.#inventory;
  }
  pick(itemId) {
    const idx = this.#inventory.findIndex((inventoryItem) => String(inventoryItem.id) === String(itemId));
    if (idx !== -1) {
      return this.#inventory.splice(idx, 1)[0] || null;
    } else {
      return null;
    }
  }
  get isBuilding() {
    return this instanceof Building;
  }
  get exploreBonus() {
    return 10;
  }
  get lock() {
    return this.#lock;
  }
  get requiredAccessLevel() {
    return this.#requiredAccessLevel;
  }
  setLock(lock) {
    this.#lock = lock;
  }
  setRequiredAccessLevel(level) {
    this.#requiredAccessLevel = level;
  }
  get stormId() {
    return this.#stormId;
  }
  setStormId(id) {
    this.#stormId = id;
  }
  setInventory(items) {
    this.#inventory = items;
  }
  canEnter(player) {
    if (player instanceof Patrol && this.isAdministrative) {
      return true;
    }
    if (this.#requiredAccessLevel > 0) {
      const hasAccess = player.inventory.some((item) => {
        if (!(item instanceof KeyCard))
          return false;
        if (item.locationId && String(item.locationId) === String(this.id))
          return true;
        return item.level >= this.#requiredAccessLevel;
      });
      if (!hasAccess)
        return false;
    }
    if (this.#lock) {
      if (this.#lock.ownerId && String(this.#lock.ownerId) === String(player.id)) {
        return true;
      }
      return false;
    }
    return true;
  }
  get isAdministrative() {
    return false;
  }
}

class Room extends Location {
  getLootProbabilities() {
    return { junk: 0.2, consumable: 0.1 };
  }
  getLockProbability() {
    return 0;
  }
}

class Building extends Location {
  getLootProbabilities() {
    return { junk: 0.3, tool: 0.1, consumable: 0.1 };
  }
  getLockProbability() {
    return 0.1;
  }
}

class AdminBuilding extends Building {
  get isAdministrative() {
    return true;
  }
  getLootProbabilities() {
    return { junk: 0.1, tool: 0.3, access: 0.2 };
  }
}

class SpecialBuilding extends Building {
  getLootProbabilities() {
    return { tool: 0.2, chemistry: 0.3, access: 0.1 };
  }
  getLockProbability() {
    return 0.3;
  }
}

class ResidentialBuilding extends Building {
  getLootProbabilities() {
    return { junk: 0.4, consumable: 0.3 };
  }
  getLockProbability() {
    return 0.2;
  }
}

class Street extends Location {
  getLootProbabilities() {
    return { junk: 0.1 };
  }
  getLockProbability() {
    return 0;
  }
}

class City extends Location {
  getLootProbabilities() {
    return {};
  }
  getLockProbability() {
    return 0;
  }
}
// entities/stalker.ts
class Stalker extends Player {
  #detentionTimer = 0;
  #status = "free";
  #activePatrol = null;
  #pendingBribe = [];
  get isDetained() {
    return this.#status !== "free";
  }
  onDetained(patrol) {
    this.#status = "detained";
    this.#activePatrol = patrol;
    this.#detentionTimer = 0;
  }
  surrender() {
    if (!this.#activePatrol)
      return;
    this.rank(-10);
    this.#activePatrol.onSurrender(this);
    this.#resetDetention();
  }
  offerBribe(items) {
    if (this.#status !== "detained" || !this.#activePatrol)
      return;
    if (items.length < 1 || items.length > 10) {
      throw new Error("Bribe must be between 1 and 10 items");
    }
    const hasItems = items.every((item) => this._inventory.includes(item));
    if (!hasItems)
      throw new Error("Not all items are in inventory");
    this.#pendingBribe = items;
    this.#status = "offering_bribe";
    this.#detentionTimer = 0;
    this.#activePatrol.onBribeOffered(this, items);
  }
  onBribeAccepted() {
    this.removeItems(this.#pendingBribe);
    this.rank(5);
    this.#resetDetention();
  }
  onBribeDeclined() {
    this.surrender();
  }
  #resetDetention() {
    this.#status = "free";
    this.#activePatrol = null;
    this.#pendingBribe = [];
    this.#detentionTimer = 0;
  }
  onTick(delta) {
    super.onTick(delta);
    if (this.#status === "detained") {
      this.#detentionTimer += delta;
      if (this.#detentionTimer >= 1e4) {
        this.surrender();
      }
    }
  }
  pickFinding(itemId) {
    const item = this._location.pick(itemId);
    if (item) {
      this._inventory.push(item);
    }
  }
  explore() {
    if (this._location.isBuilding) {
      this.rank(this._location.exploreBonus);
    }
  }
  squat(lock) {
    if (!(this._location instanceof ResidentialBuilding)) {
      throw new Error("Can only squat in residential buildings");
    }
    if (this._location.lock) {
      throw new Error("Building is already locked");
    }
    this._location.setLock(lock);
  }
  breakIn(building) {
    if (!building.lock)
      return;
    const lockpick = this._inventory.find((item) => item instanceof Lockpick);
    if (!lockpick) {
      throw new Error("No lockpick in inventory");
    }
    this.removeItems([lockpick]);
    building.setLock(null);
  }
}
// entities/lock.ts
class LockID extends ID {
  __type = Symbol.for("LockID");
}

class Lock {
  id;
  type;
  ownerId;
  constructor(id, type, ownerId) {
    this.id = id;
    this.type = type;
    this.ownerId = ownerId;
  }
}
// entities/transaction.ts
class TransactionID extends ID {
  __type = Symbol.for("TransactionID");
}

class Transaction {
  id;
  amount;
  type;
  timestamp;
  description;
  constructor(id, amount, type, timestamp, description) {
    this.id = id;
    this.amount = amount;
    this.type = type;
    this.timestamp = timestamp;
    this.description = description;
  }
}
// services/loot-generator.ts
class LootGeneratorService {
  worldRepo;
  itemRepo;
  templates = [];
  constructor(worldRepo, itemRepo) {
    this.worldRepo = worldRepo;
    this.itemRepo = itemRepo;
  }
  setTemplates(templates) {
    this.templates = templates;
  }
  refreshIfNeeded(location, worldState) {
    if (location.stormId === worldState.currentStormId) {
      return;
    }
    if (this.worldRepo.isParentSquated(location.id)) {
      location.setStormId(worldState.currentStormId);
      return;
    }
    location.setInventory([]);
    location.setLock(null);
    location.setStormId(worldState.currentStormId);
    this.trySpawnLock(location);
    this.spawnLoot(location);
  }
  trySpawnLock(location) {
    if (!(location instanceof ResidentialBuilding))
      return;
    const prob = location.getLockProbability();
    if (Math.random() < prob) {
      const locked = this.worldRepo.getLockedBuildingsCount();
      const total = this.worldRepo.getTotalResidentialCount();
      if (total === 0 || locked / total < 0.1) {
        const lock = new Lock(new LockID(`lock-${location.id.toString()}-${Date.now()}`), "mechanical");
        location.setLock(lock);
      }
    }
  }
  spawnLoot(location) {
    const probs = location.getLootProbabilities();
    const items = [];
    for (const [category, prob] of Object.entries(probs)) {
      if (Math.random() < prob) {
        items.push(this.createItem(category, location));
      }
    }
    location.setInventory(items);
  }
  createItem(category, location) {
    const id = new ItemID(crypto.randomUUID());
    if (category === "access") {
      const existingCount = this.itemRepo.countKeyCardsForLocation(location.id);
      if (existingCount >= 2) {
        return this.spawnFallback(id, "junk");
      }
      return new KeyCard({ id, typeId: "bunker_key", weight: 0.1, rarity: 2 /* Rare */ }, 1, location.id);
    }
    const possible = this.templates.filter((t) => t.category === category);
    if (possible.length === 0) {
      return this.spawnFallback(id, category);
    }
    const template = possible[Math.floor(Math.random() * possible.length)];
    if (!template) {
      return this.spawnFallback(id, category);
    }
    return new Finding({
      id,
      typeId: template.typeId,
      weight: template.weight || 0.1 + Math.random() * 0.5,
      rarity: template.rarity,
      category: template.category
    });
  }
  spawnFallback(id, category) {
    return new Finding({
      id,
      typeId: "trash",
      weight: 1,
      rarity: 0 /* Common */,
      category
    });
  }
}
// services/world-service.ts
class WorldService {
  worldRepo;
  constructor(worldRepo) {
    this.worldRepo = worldRepo;
  }
  isOrderBonusEligible() {
    const stalkers = this.worldRepo.getStalkerCount();
    const patrol = this.worldRepo.getPatrolCount();
    return stalkers < patrol;
  }
}
// services/economy-service.ts
var MS_IN_DAY = 24 * 60 * 60 * 1000;
var DAILY_EXPENSE = 100;
var HEALTH_LOSS_STARVATION = 1 / 7;
var PATROL_SALARY = 80;
var PATROL_ORDER_BONUS = 20;

class EconomyService {
  processDailyExpenses(player, currentTimestamp, context) {
    if (player.isDead)
      return;
    const elapsed = currentTimestamp - player.lastExpenseAt;
    if (elapsed < MS_IN_DAY)
      return;
    const days = Math.floor(elapsed / MS_IN_DAY);
    let daysProcessed = 0;
    for (let i = 0;i < days; i++) {
      this.applyDailyCycle(player, context);
      daysProcessed++;
      if (player.isDead)
        break;
    }
    player.setLastExpenseAt(player.lastExpenseAt + daysProcessed * MS_IN_DAY);
  }
  applyDailyCycle(player, context) {
    if (player instanceof Patrol) {
      player.addMoney(PATROL_SALARY);
      if (context?.isOrderBonusEligible) {
        player.addMoney(PATROL_ORDER_BONUS);
      }
    }
    const success = player.withdrawMoney(DAILY_EXPENSE);
    if (!success) {
      player.applyStarvingEffect(HEALTH_LOSS_STARVATION);
    }
  }
}
// services/trade-service.ts
var BASE_PRICES = {
  junk: 10,
  tool: 50,
  chemistry: 100,
  consumable: 20,
  access: 200
};
var RARITY_MULTIPLIERS = {
  [0 /* Common */]: 1,
  [1 /* Uncommon */]: 2.5,
  [2 /* Rare */]: 10,
  [3 /* Legendary */]: 50
};

class TradeService {
  calculatePrice(item) {
    const base = BASE_PRICES[item.category] || 0;
    const multiplier = RARITY_MULTIPLIERS[item.rarity] || 1;
    return Math.floor(base * multiplier);
  }
  sellItems(player, items) {
    const hasAll = items.every((item) => player.inventory.includes(item));
    if (!hasAll) {
      throw new Error("Not all items are in player inventory");
    }
    const totalPrice = items.reduce((sum, item) => sum + this.calculatePrice(item), 0);
    player.removeItems(items);
    player.addMoney(totalPrice);
    return totalPrice;
  }
}
export {
  WorldService,
  TransactionID,
  Transaction,
  TradeService,
  Street,
  Stalker,
  SpecialBuilding,
  Room,
  ResidentialBuilding,
  Rarity,
  RARITY_MULTIPLIERS,
  PlayerID,
  Player,
  Patrol,
  PATROL_SALARY,
  PATROL_ORDER_BONUS,
  Medkit,
  MS_IN_DAY,
  LootGeneratorService,
  Lockpick,
  LockID,
  Lock,
  LocationID,
  Location,
  KeyCard,
  ItemID,
  ID,
  HEALTH_LOSS_STARVATION,
  Food,
  Finding,
  Filter,
  EnergyDrink,
  EconomyService,
  ENERGY_REGEN_PER_MS,
  ENERGY_PER_ACTION,
  DAILY_EXPENSE,
  Consumable,
  CollectionSet,
  City,
  COLLECTION_IDS,
  Building,
  BASE_PRICES,
  AdminBuilding,
  AccessTool
};
